import { useCallback } from 'react';
import { useSnackbar } from '../context/snackbarContext';
import { useTranslation } from 'react-i18next';

/**
 * Centralized API error handler hook that provides consistent error handling
 * across all API operations with user-friendly translated messages.
 */
export const useApiErrorHandler = () => {
    const { showSnackbar } = useSnackbar();
    const { t } = useTranslation();

    const handleApiError = useCallback((error: any, context?: string) => {
        console.error(`API Error${context ? ` (${context})` : ''}:`, error);

        // Extract error information
        const apiError = error?.response?.data;
        const statusCode = error?.response?.status;
        const errorType = apiError?.errorType;
        const errorCode = apiError?.code;
        const errorMessage = apiError?.message;

        // Determine error category and message
        let userMessage: string;

        // 1. Handle image generation errors (regardless of context)
        if (errorType && ['safety_filter', 'api_server_error', 'server_error'].includes(errorType)) {
            const translationKey = `errors.imageGeneration.${errorType}`;
            userMessage = t(translationKey, {
                defaultValue: t('errors.imageGeneration.unknown')
            });
        }
        // 2. Handle specific error codes (like style resolution errors)
        else if (errorCode) {
            const translationKey = `errors.styleResolution.${errorCode}`;
            userMessage = t(translationKey, {
                defaultValue: t('errors.general.unknown')
            });
        }
        // 3. Handle HTTP status codes
        else if (statusCode) {
            const translationKey = `errors.http.${statusCode}`;
            userMessage = t(translationKey, {
                defaultValue: t('errors.general.unknown')
            });
        }
        // 4. Handle network errors - DON'T show snackbar, let service worker handle
        else if (error?.message?.includes('Network Error') || error?.code === 'ERR_NETWORK') {
            // Network errors should be handled silently by service worker background sync
            // Don't show snackbar to user - they'll see the item appear when connection is restored
            return;
        }
        // 5. Handle timeout errors - DON'T show snackbar, let service worker handle
        else if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
            // Timeout errors should be handled silently by service worker background sync
            // Don't show snackbar to user - they'll see the item appear when connection is restored
            return;
        }
        // 6. Handle queued requests (offline mode)
        else if (error?.name === 'QueuedRequest' && error?.isQueued) {
            // Don't show error for queued requests - they'll be handled by background sync
            return;
        }
        // 7. Fallback to general unknown error
        else {
            userMessage = t('errors.general.unknown');
        }

        showSnackbar(userMessage, 'error');
    }, [showSnackbar, t]);

    return { handleApiError };
};
